// ---------------------------------------------------------------
// TagAlong Fleet — company accounts, drivers, and vehicle assignments.
//
// A fleet account is a COMPANY, not a person: it owns vehicles (through the
// same Traccar devices the consumer app uses), a roster of drivers, and the
// mapping of which driver is in which vehicle. Staff logins come later.
//
// Auth goes through the backend (/auth/fleet/*), exactly like brokers and
// family members — the server salts and hashes passwords and issues the JWT.
// Fleet data lives in Postgres via /fleet/data.
// ---------------------------------------------------------------
import { API_BASE } from './config';
import { getJwt } from './traccar';

const ME_KEY = 'ta-fleet-me';

const cacheMe = (u) => {
  try { if (u) localStorage.setItem(ME_KEY, JSON.stringify(u)); else localStorage.removeItem(ME_KEY); }
  catch { /* ignore */ }
};
const setJwt = (t) => {
  try { if (t) localStorage.setItem('ta-jwt', t); else localStorage.removeItem('ta-jwt'); }
  catch { /* ignore */ }
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  ...(getJwt() ? { Authorization: `Bearer ${getJwt()}` } : {}),
});

// ---- accounts ----
export async function createFleetAccount({ company, name, email, password }) {
  const e = (email || '').toLowerCase().trim();
  if (!e || !password) throw new Error('Email and password are required.');
  if (!company) throw new Error('Company name is required.');
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/auth/fleet/signup`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ company, name, email: e, password }),
  });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.error || 'Could not create the account.');
  }
  const u = await r.json();
  cacheMe(u); setJwt(u.token);
  return u;
}

export async function fleetLogin(email, password) {
  const e = (email || '').toLowerCase().trim();
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/auth/fleet/login`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: e, password }),
  });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.error || 'Wrong email or password.');
  }
  const u = await r.json();
  cacheMe(u); setJwt(u.token);
  return u;
}

export function fleetLogout() {
  try { fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' }); } catch { /* ignore */ }
  cacheMe(null); setJwt('');
}

export function getFleetSession() {
  try { return JSON.parse(localStorage.getItem(ME_KEY)); } catch { return null; }
}

// ---- fleet data (drivers / assignments / staff) ----
const BLANK = { drivers: [], assignments: {}, staff: [] };

export async function loadFleetData() {
  if (!API_BASE) return BLANK;
  const r = await fetch(`${API_BASE}/fleet/data`, { credentials: 'include', headers: authHeaders() });
  if (!r.ok) throw new Error(r.status === 403 ? 'Not signed in as a fleet account.' : 'Could not load fleet data.');
  const d = await r.json();
  return { drivers: d.drivers || [], assignments: d.assignments || {}, staff: d.staff || [] };
}

export async function saveFleetData(data) {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/fleet/data`, {
    method: 'PUT', credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  if (!r.ok) throw new Error('Could not save.');
  return r.json();
}

// ---- live vehicles from the company's own telematics ----
// A fleet company usually already has trackers (Samsara, in Florida Beauty's
// case — 170 vehicles). We show THEIR fleet rather than asking them to move
// hardware. The token lives on our server and never reaches the browser.
export async function loadLiveFleet({ includeDormant = false } = {}) {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/fleet/live${includeDormant ? '?includeDormant=1' : ''}`, {
    credentials: 'include', headers: authHeaders(),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'Could not load the fleet.');
  return d;
}

// Drivers come FROM Samsara, along with which truck each is in. We never ask a
// company to re-key a roster they already maintain.
export async function loadTelematicsDrivers() {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/fleet/drivers`, { credentials: 'include', headers: authHeaders() });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'Could not load drivers.');
  return d;
}

// Full per-vehicle telemetry: fuel, odometer, RPM, coolant, battery, DEF,
// ambient temp, engine hours, fault codes.
export async function loadVehiclesFull() {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/fleet/vehicles/full`, { credentials: 'include', headers: authHeaders() });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'Could not load vehicle detail.');
  return d;
}

// Probes every Samsara endpoint and reports what this account actually exposes.
export async function discoverTelematics() {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/fleet/discover`, { credentials: 'include', headers: authHeaders() });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'Could not probe the account.');
  return d;
}

// Outbound LTL runs in progress — route + live truck + driver hours, joined.
export async function loadRuns() {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/fleet/runs`, { credentials: 'include', headers: authHeaders() });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'Could not load runs.');
  return d;
}

export async function loadHos() {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/fleet/hos`, { credentials: 'include', headers: authHeaders() });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'Could not load hours of service.');
  return d;
}

// The AI dispatcher's review — what needs a human right now.
export async function loadAttention() {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/dispatch/attention`, { credentials: 'include', headers: authHeaders() });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'Could not load the review.');
  return d;
}

export async function getTelematicsStatus() {
  if (!API_BASE) return { connected: false };
  try {
    const r = await fetch(`${API_BASE}/fleet/telematics`, { credentials: 'include', headers: authHeaders() });
    return r.ok ? r.json() : { connected: false };
  } catch { return { connected: false }; }
}

export async function connectSamsara(samsaraToken) {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/fleet/telematics`, {
    method: 'PUT', credentials: 'include', headers: authHeaders(),
    body: JSON.stringify({ samsaraToken }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'Could not connect.');
  return d;
}

// ---- claiming vehicles into the fleet ----
// The company enters the per-vehicle code the owner gave them. The server does
// the actual linking (and the enforcement) — this is only the request.
export async function claimVehicle(code) {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/fleet/claim`, {
    method: 'POST', credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify({ code }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'Could not add the vehicle.');
  return d;
}

export async function releaseVehicle(deviceId) {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/fleet/release`, {
    method: 'POST', credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify({ deviceId }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'Could not remove the vehicle.');
  return d;
}

// ---- driver helpers (pure — callers persist with saveFleetData) ----
export const newDriverId = () => `D${Date.now()}${Math.floor(Math.random() * 1000)}`;

export function addDriver(data, { name, phone, license, email }) {
  if (!name || !name.trim()) throw new Error('Driver name is required.');
  const driver = {
    id: newDriverId(),
    name: name.trim(),
    phone: (phone || '').trim(),
    license: (license || '').trim(),
    email: (email || '').trim().toLowerCase(),
    createdAt: new Date().toISOString(),
  };
  return { ...data, drivers: [...data.drivers, driver] };
}

export function removeDriver(data, driverId) {
  // also clear any vehicle they were assigned to, so no dangling references
  const assignments = { ...data.assignments };
  Object.keys(assignments).forEach((devId) => {
    if (assignments[devId] === driverId) delete assignments[devId];
  });
  return { ...data, drivers: data.drivers.filter((d) => d.id !== driverId), assignments };
}

export function assignDriver(data, deviceId, driverId) {
  const assignments = { ...data.assignments };
  if (driverId) assignments[String(deviceId)] = driverId;
  else delete assignments[String(deviceId)];
  return { ...data, assignments };
}

export const driverFor = (data, deviceId) =>
  data.drivers.find((d) => d.id === data.assignments[String(deviceId)]) || null;

// Which vehicles is this driver currently in? (normally one, but not enforced —
// a driver swapping vehicles mid-day shouldn't be blocked by the data model.)
export const vehiclesFor = (data, driverId) =>
  Object.keys(data.assignments).filter((devId) => data.assignments[devId] === driverId);
